import React, { useMemo, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  ComposedChart,
  Area,
  AreaChart,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ReferenceArea,
  LabelList,
} from "recharts";

/* =========================================================================
   SINGLE SOURCE OF TRUTH
   The component renders live data passed via the `data` prop. EMPTY_DATA is a
   blank, zero-value shape used ONLY to keep the hooks below crash-safe when no
   data is available — in that case the component shows a "connect your sheet"
   state and never renders any placeholder / sample numbers.
   ========================================================================= */
const EMPTY_DATA = {
  company: "",
  title: "Financial Dashboard",
  subtitle: "",
  selectedMonth: "",
  actualThrough: "",
  lastSync: null,
  months: [],
  monthly: {
    revenue: [], cogs: [], grossProfit: [], netIncome: [], grossMarginPct: [],
    expensesByCategory: {},
    totalAssets: [], totalLiabilities: [], totalEquity: [],
    currentAssets: [], currentLiabilities: [], inventory: [],
  },
  ytd: {
    income: 0, incomePriorYtd: null, pctOfTotalIncome: 0, periodLabel: "", mode: "trailing_12",
    grossProfit: 0, netIncome: 0, grossMarginPct: 0,
    cogs: 0, cogsPctOfIncome: 0, grossRevenue: 0, netRevenue: 0,
    revenueBySource: [], expenseCategories: [], topVendors: [],
  },
  targets: { grossMarginPct: 60 },
};

/* =========================================================================
   COLOR PALETTE — used consistently across every chart
   ========================================================================= */
const COLORS = {
  indigo: "#6C6FE8",  // revenue / total assets / channel sales
  teal: "#2DBE9A",    // gross profit / total equity / refunds
  violet: "#A78BFA",  // net income
  coral: "#F26D6D",   // liabilities / negative
  blue: "#4D96F0",    // current assets
  amber: "#F5A623",
  pink: "#EC4899",
  slate: "#94A3B8",
};

// Cyclical palette for category series with many members.
const CATEGORY_PALETTE = [
  COLORS.indigo, COLORS.teal, COLORS.violet, COLORS.coral,
  COLORS.blue, COLORS.amber, COLORS.pink, COLORS.slate,
  "#0EA5E9", "#84CC16",
];

/* =========================================================================
   FORMAT HELPERS
   ========================================================================= */
const fmtUSD = (n) => {
  if (n == null || isNaN(n)) return "—";
  const sign = n < 0 ? "-" : "";
  return sign + "$" + Math.round(Math.abs(n)).toLocaleString("en-US");
};

const fmtK = (n) => {
  if (n == null || isNaN(n)) return "";
  const sign = n < 0 ? "-" : "";
  const a = Math.abs(n);
  if (a >= 1000) return sign + "$" + (a / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  return sign + "$" + a.toFixed(0);
};

const fmtPct = (n) => {
  if (n == null || isNaN(n)) return "—";
  return n.toFixed(1) + "%";
};

const shortMonth = (m) => (m ? m.replace(/ 20\d\d$/, "") : m); // "Jun 2026" -> "Jun"

/* =========================================================================
   SHARED CHART TOOLTIP — matches the card styling
   ========================================================================= */
function ChartTooltip({ active, payload, label, valueFmt = fmtUSD, percent = false }) {
  if (!active || !payload || !payload.length) return null;
  const rows = payload.filter((p) => p.value != null);
  if (!rows.length) return null;
  return (
    <div className="fd-tooltip">
      {label != null && <div className="fd-tooltip-title">{label}</div>}
      {rows.map((p, i) => (
        <div className="fd-tooltip-row" key={i}>
          <span className="fd-tooltip-dot" style={{ background: p.color || p.fill || p.stroke }} />
          <span className="fd-tooltip-name">{p.name}</span>
          <span className="fd-tooltip-val">
            {percent ? fmtPct(p.value) : valueFmt(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

/* =========================================================================
   SMALL PRESENTATIONAL PIECES
   ========================================================================= */
function SectionHeading({ title, description }) {
  return (
    <div className="fd-section-head">
      <h2 className="fd-section-title">{title}</h2>
      {description && <p className="fd-section-desc">{description}</p>}
    </div>
  );
}

function Card({ title, description, children, className = "", empty = false, emptyLabel }) {
  return (
    <div className={"fd-card " + className}>
      {(title || description) && (
        <div className="fd-card-head">
          {title && <h3 className="fd-card-title">{title}</h3>}
          {description && <p className="fd-card-desc">{description}</p>}
        </div>
      )}
      {empty ? <EmptyState label={emptyLabel} /> : children}
    </div>
  );
}

function KpiCard({ label, icon, value, deltaText, deltaTone, tag, valueTone }) {
  return (
    <div className="fd-kpi">
      <div className="fd-kpi-top">
        <span className="fd-kpi-label">{label}</span>
        <span className="fd-kpi-icon" aria-hidden="true">{icon}</span>
      </div>
      <div className={"fd-kpi-value" + (valueTone ? " fd-tone-" + valueTone : "")}>{value}</div>
      {deltaText && (
        <div className={"fd-kpi-delta" + (deltaTone ? " fd-tone-" + deltaTone : "")}>{deltaText}</div>
      )}
      {tag && <div className="fd-kpi-tag">{tag}</div>}
    </div>
  );
}

// Tiny inline glyphs (no extra deps) — purely decorative.
const ICONS = {
  income: "◷",
  profit: "▦",
  net: "＄",
  margin: "％",
};

// Shown in place of a chart when its underlying series has no data (e.g. a
// business with no inventory, or no connected balance sheet).
function EmptyState({ label = "No data available" }) {
  return (
    <div className="fd-empty">
      <span className="fd-empty-ico" aria-hidden="true">▤</span>
      <span>{label}</span>
    </div>
  );
}

/* =========================================================================
   MAIN COMPONENT
   ========================================================================= */
export default function FinancialDashboard({ data }) {
  // Live data arrives via the `data` prop. When it's absent, the component
  // renders a "connect your sheet" empty state (below) — never sample numbers.
  // EMPTY_DATA only keeps the hooks crash-safe until real data is present.
  const hasData = !!(data && Array.isArray(data.months) && data.months.length);
  const D = data || EMPTY_DATA;
  const [selectedMonth, setSelectedMonth] = useState(D.selectedMonth);

  const selIndex = D.months.indexOf(selectedMonth);
  const actualIndex = D.months.indexOf(D.actualThrough);
  const m = D.monthly;

  // The one calendar-mode user sees "YTD"; everyone on trailing-12 sees "LTM"
  // (Last Twelve Months). Driven by ytd.mode from the backend.
  const ytdMode = D.ytd.mode || "trailing_12";
  const ytdAbbr = ytdMode === "calendar" ? "YTD" : "LTM";
  const ytdFull = ytdMode === "calendar" ? "Year-to-Date" : "Last Twelve Months";

  /* ---------- Derived: indices of actual (non-null revenue) months ---------- */
  const actualMonthIdxs = useMemo(
    () => D.months.map((_, i) => i).filter((i) => m.revenue[i] != null),
    [D.months, m.revenue]
  );

  const categoryNames = useMemo(() => Object.keys(m.expensesByCategory), [m.expensesByCategory]);
  const catColor = (name) => CATEGORY_PALETTE[categoryNames.indexOf(name) % CATEGORY_PALETTE.length];

  /* ---------- Monthly Performance KPIs (read at selIndex) ---------- */
  const monthlyKpis = useMemo(() => {
    const i = selIndex;
    const prev = i - 1;
    const rev = m.revenue[i];
    const gp = m.grossProfit[i];
    const ni = m.netIncome[i];
    const gm = m.grossMarginPct[i];

    // Income % vs prior month
    let incomeDelta = null, incomeTone = "muted";
    if (prev >= 0 && m.revenue[prev] != null && rev != null && m.revenue[prev] !== 0) {
      const pct = ((rev - m.revenue[prev]) / m.revenue[prev]) * 100;
      incomeDelta = (pct >= 0 ? "+" : "") + pct.toFixed(1) + "% vs prior month";
      incomeTone = pct >= 0 ? "pos" : "neg";
    }

    // Gross profit sub-label = margin % of income
    const gpSub = gm != null ? fmtPct(gm) + " of income" : null;

    // Net income % of income
    let niSub = null, niTone = "muted";
    if (ni != null && rev) {
      const pct = (ni / rev) * 100;
      niSub = fmtPct(pct) + " of income";
      niTone = ni >= 0 ? "pos" : "neg";
    }
    const niValueTone = ni != null && ni < 0 ? "neg" : null;

    // Gross margin ± pp vs prior month
    let gmDelta = null, gmTone = "muted";
    if (prev >= 0 && m.grossMarginPct[prev] != null && gm != null) {
      const pp = gm - m.grossMarginPct[prev];
      gmDelta = (pp >= 0 ? "+" : "") + pp.toFixed(1) + " pp vs prior month";
      gmTone = pp >= 0 ? "pos" : "neg";
    }

    return {
      income: { value: fmtUSD(rev), delta: incomeDelta, tone: incomeTone },
      grossProfit: { value: fmtUSD(gp), delta: gpSub, tone: "muted" },
      netIncome: { value: fmtUSD(ni), delta: niSub, tone: niTone, valueTone: niValueTone },
      grossMargin: { value: gm != null ? fmtPct(gm) : "—", delta: gmDelta, tone: gmTone },
    };
  }, [selIndex, m]);

  /* ---------- YTD KPIs ---------- */
  const ytdKpis = useMemo(() => {
    const y = D.ytd;
    const incomeDeltaPct =
      y.incomePriorYtd ? ((y.income - y.incomePriorYtd) / y.incomePriorYtd) * 100 : null;
    const niPctOfIncome = y.income ? (y.netIncome / y.income) * 100 : null;
    return {
      income: {
        value: fmtUSD(y.income),
        delta:
          (incomeDeltaPct != null
            ? (incomeDeltaPct >= 0 ? "+" : "") + incomeDeltaPct.toFixed(1) + "% vs prior YTD"
            : null),
        tone: incomeDeltaPct != null && incomeDeltaPct >= 0 ? "pos" : "neg",
        sub: fmtPct(y.pctOfTotalIncome) + " of total income",
      },
      grossProfit: {
        value: fmtUSD(y.grossProfit),
        delta: fmtPct((y.grossProfit / y.income) * 100) + " of income",
        tone: "muted",
      },
      netIncome: {
        value: fmtUSD(y.netIncome),
        delta: (niPctOfIncome != null ? fmtPct(niPctOfIncome) + " of income" : null),
        tone: y.netIncome >= 0 ? "pos" : "neg",
      },
      grossMargin: {
        value: fmtPct(y.grossMarginPct),
        delta: "GP " + fmtK(y.grossProfit) + " / Income " + fmtK(y.income),
        tone: "muted",
      },
    };
  }, [D.ytd]);

  const ytdTag = useMemo(() => {
    // The backend labels the YTD window (calendar vs trailing-12); fall back to
    // deriving it from the actual months if not provided.
    if (D.ytd.periodLabel) return D.ytd.periodLabel;
    const first = shortMonth(D.months[actualMonthIdxs[0]]);
    const last = shortMonth(D.months[actualMonthIdxs[actualMonthIdxs.length - 1]]);
    const year = (D.months[actualMonthIdxs[actualMonthIdxs.length - 1]] || "").match(/20\d\d/);
    return first + " – " + last + (year ? " " + year[0] : "");
  }, [D.ytd.periodLabel, actualMonthIdxs, D.months]);

  /* ---------- P&L grouped bar data (actual months only) ---------- */
  const pnlData = useMemo(
    () =>
      actualMonthIdxs.map((i) => ({
        month: shortMonth(D.months[i]),
        fullMonth: D.months[i],
        revenue: m.revenue[i],
        grossProfit: m.grossProfit[i],
        netIncome: m.netIncome[i],
        selected: D.months[i] === selectedMonth,
      })),
    [actualMonthIdxs, D.months, m, selectedMonth]
  );
  const selectedShort = shortMonth(selectedMonth);

  /* ---------- Expense Categories (selected month), desc ---------- */
  const expenseCurrentMonth = useMemo(() => {
    return categoryNames
      .map((name) => ({ name, value: m.expensesByCategory[name][selIndex] }))
      .filter((d) => d.value != null)
      .sort((a, b) => b.value - a.value);
  }, [categoryNames, m.expensesByCategory, selIndex]);

  /* ---------- Monthly Expense Trend (stacked, actual months) ---------- */
  const expenseTrend = useMemo(
    () =>
      actualMonthIdxs.map((i) => {
        const row = { month: shortMonth(D.months[i]) };
        categoryNames.forEach((name) => {
          row[name] = m.expensesByCategory[name][i];
        });
        return row;
      }),
    [actualMonthIdxs, D.months, categoryNames, m.expensesByCategory]
  );

  /* ---------- Balance sheet grouped bars (all 12 months) ---------- */
  const balanceData = useMemo(
    () =>
      D.months.map((mo, i) => ({
        month: shortMonth(mo),
        totalAssets: m.totalAssets[i],
        totalLiabilities: m.totalLiabilities[i],
        totalEquity: m.totalEquity[i],
        projected: i > actualIndex,
      })),
    [D.months, m, actualIndex]
  );

  /* ---------- Inventory area+line (12 months) ---------- */
  const inventoryData = useMemo(
    () =>
      D.months.map((mo, i) => ({
        month: shortMonth(mo),
        inventory: m.inventory[i],
        projected: i > actualIndex,
      })),
    [D.months, m.inventory, actualIndex]
  );
  const inventoryAvg = useMemo(() => {
    const vals = m.inventory.filter((v) => v != null);
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
  }, [m.inventory]);

  /* ---------- Current assets vs liabilities (12 months) ---------- */
  const currentData = useMemo(
    () =>
      D.months.map((mo, i) => ({
        month: shortMonth(mo),
        currentAssets: m.currentAssets[i],
        currentLiabilities: m.currentLiabilities[i],
        projected: i > actualIndex,
      })),
    [D.months, m, actualIndex]
  );
  const currentRatio =
    m.currentLiabilities[selIndex] ? m.currentAssets[selIndex] / m.currentLiabilities[selIndex] : null;

  /* ---------- Revenue by source donut ---------- */
  const revenueSourceTotal = useMemo(
    () => D.ytd.revenueBySource.reduce((a, b) => a + b.value, 0),
    [D.ytd.revenueBySource]
  );
  // Backend returns up to 6 revenue sources; keep each slice distinctly colored.
  const donutColors = [COLORS.indigo, COLORS.teal, COLORS.coral, COLORS.blue, COLORS.amber, COLORS.violet];

  /* ---------- Gross margin % by month (actual) ---------- */
  const marginData = useMemo(
    () =>
      actualMonthIdxs.map((i) => ({
        month: shortMonth(D.months[i]),
        grossMarginPct: m.grossMarginPct[i],
      })),
    [actualMonthIdxs, D.months, m.grossMarginPct]
  );

  /* ---------- Expense Categories YTD desc ---------- */
  const expenseYtd = useMemo(
    () => [...D.ytd.expenseCategories].sort((a, b) => b.value - a.value),
    [D.ytd.expenseCategories]
  );

  /* ---------- Top vendors YTD (backend already sorts desc) ---------- */
  const vendorYtd = useMemo(() => [...(D.ytd.topVendors || [])], [D.ytd.topVendors]);

  const lastSyncLabel = useMemo(() => {
    try {
      return new Date(D.lastSync).toLocaleDateString("en-US", {
        month: "short", day: "numeric", year: "numeric",
      });
    } catch {
      return D.lastSync;
    }
  }, [D.lastSync]);

  const axisProps = {
    tick: { fill: "#9CA3AF", fontSize: 12 },
    axisLine: { stroke: "#ECECEC" },
    tickLine: false,
  };

  /* ---------- Per-chart "has data" flags (drives the empty state) ---------- */
  const someVal = (arr) => Array.isArray(arr) && arr.some((v) => v != null && v !== 0);
  const has = {
    pnl: someVal(m.revenue) || someVal(m.grossProfit) || someVal(m.netIncome),
    expenseMonth: expenseCurrentMonth.some((d) => d.value),
    expenseTrend: categoryNames.some((n) => someVal(m.expensesByCategory[n])),
    balance: someVal(m.totalAssets) || someVal(m.totalLiabilities) || someVal(m.totalEquity),
    inventory: someVal(m.inventory),
    current: someVal(m.currentAssets) || someVal(m.currentLiabilities),
    revenueSource: D.ytd.revenueBySource.length > 0 && revenueSourceTotal > 0,
    margin: marginData.some((d) => d.grossMarginPct != null),
    expenseYtd: expenseYtd.length > 0,
    vendors: vendorYtd.length > 0,
  };

  if (!hasData) {
    return (
      <div className="fin-dash fd-empty-shell">
        <style>{CSS}</style>
        <div className="fd-noconnect">
          <div className="fd-noconnect-ico" aria-hidden="true">▤</div>
          <div className="fd-noconnect-title">No financial data to display yet</div>
          <div className="fd-noconnect-sub">
            Connect your Google Sheet to see your live financial dashboard.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fin-dash">
      <style>{CSS}</style>

      {/* ===================== HEADER ===================== */}
      <header className="fd-header">
        <div className="fd-header-left">
          <div className="fd-company">{D.company}</div>
          <h1 className="fd-title">{D.title}</h1>
          <p className="fd-disclaimer">
            Disclaimer: This dashboard reflects P&amp;L and balance sheet data pulled from
            QuickBooks Online and is only as accurate as the underlying bookkeeping. Figures
            may not reflect same-day transactions due to timing of categorization, accruals,
            and reconciliations. All information is unaudited, for internal management use
            only, and subject to change as bookkeeping is finalized.
          </p>
        </div>
        <div className="fd-header-right">
          <div className="fd-filter">
            <span className="fd-filter-ico" aria-hidden="true">⛛</span>
            <span>Filter</span>
          </div>
          <label className="fd-select-wrap">
            <select
              className="fd-select"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              {D.months.map((mo) => (
                <option key={mo} value={mo}>{mo}</option>
              ))}
            </select>
          </label>
          <div className="fd-sync">Synced {lastSyncLabel}</div>
        </div>
      </header>

      {/* ===================== MONTHLY PERFORMANCE ===================== */}
      <SectionHeading
        title="Monthly Performance"
        description={"Key P&L metrics for " + selectedMonth + ", with change vs. the prior month."}
      />
      <div className="fd-kpi-row">
        <KpiCard
          label="Monthly Income" icon={ICONS.income}
          value={monthlyKpis.income.value}
          deltaText={monthlyKpis.income.delta} deltaTone={monthlyKpis.income.tone}
          tag={selectedMonth}
        />
        <KpiCard
          label="Monthly Gross Profit" icon={ICONS.profit}
          value={monthlyKpis.grossProfit.value}
          deltaText={monthlyKpis.grossProfit.delta} deltaTone={monthlyKpis.grossProfit.tone}
          tag={selectedMonth}
        />
        <KpiCard
          label="Monthly Net Income" icon={ICONS.net}
          value={monthlyKpis.netIncome.value} valueTone={monthlyKpis.netIncome.valueTone}
          deltaText={monthlyKpis.netIncome.delta} deltaTone={monthlyKpis.netIncome.tone}
          tag={selectedMonth}
        />
        <KpiCard
          label="Monthly Gross Margin" icon={ICONS.margin}
          value={monthlyKpis.grossMargin.value}
          deltaText={monthlyKpis.grossMargin.delta} deltaTone={monthlyKpis.grossMargin.tone}
          tag={selectedMonth}
        />
      </div>

      {/* ===================== YTD / LTM PERFORMANCE ===================== */}
      <SectionHeading
        title={ytdFull + " Performance"}
        description={"Cumulative results for " + ytdTag + "."}
      />
      <div className="fd-kpi-row">
        <KpiCard
          label={ytdAbbr + " Income"} icon={ICONS.income}
          value={ytdKpis.income.value}
          deltaText={ytdKpis.income.delta} deltaTone={ytdKpis.income.tone}
          tag={ytdTag}
        />
        <KpiCard
          label={ytdAbbr + " Gross Profit"} icon={ICONS.profit}
          value={ytdKpis.grossProfit.value}
          deltaText={ytdKpis.grossProfit.delta} deltaTone={ytdKpis.grossProfit.tone}
          tag={ytdTag}
        />
        <KpiCard
          label={ytdAbbr + " Net Income"} icon={ICONS.net}
          value={ytdKpis.netIncome.value}
          deltaText={ytdKpis.netIncome.delta} deltaTone={ytdKpis.netIncome.tone}
          tag={ytdTag}
        />
        <KpiCard
          label={ytdAbbr + " Gross Margin %"} icon={ICONS.margin}
          value={ytdKpis.grossMargin.value}
          deltaText={ytdKpis.grossMargin.delta} deltaTone={ytdKpis.grossMargin.tone}
          tag={ytdTag}
        />
      </div>

      {/* ===================== PROFIT & LOSS ANALYSIS ===================== */}
      <SectionHeading
        title="Profit & Loss Analysis"
        description="Revenue, gross profit, and net income across actual months."
      />
      <Card
        title="Monthly Revenue, Gross Profit & Net Income"
        description={"Selected month (" + selectedShort + ") highlighted with value labels."}
        empty={!has.pnl}
      >
        <div className="fd-chart fd-chart-tall">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={pnlData} margin={{ top: 24, right: 8, left: 0, bottom: 0 }} barCategoryGap="22%">
              <CartesianGrid vertical={false} stroke="#EEEEEE" />
              <XAxis dataKey="month" {...axisProps} />
              <YAxis {...axisProps} tickFormatter={fmtK} width={56} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(108,111,232,0.06)" }} />
              <Legend wrapperStyle={{ fontSize: 12, color: "#6B7280", paddingTop: 8 }} />
              <ReferenceArea x1={selectedShort} x2={selectedShort} fill={COLORS.indigo} fillOpacity={0.06} />
              <Bar isAnimationActive={false} name="Total Revenue" dataKey="revenue" fill={COLORS.indigo} radius={[5, 5, 0, 0]} maxBarSize={26}>
                <LabelList dataKey="revenue" position="top" content={(p) => <SelLabel {...p} data={pnlData} fmt={fmtK} />} />
              </Bar>
              <Bar isAnimationActive={false} name="Gross Profit" dataKey="grossProfit" fill={COLORS.teal} radius={[5, 5, 0, 0]} maxBarSize={26}>
                <LabelList dataKey="grossProfit" position="top" content={(p) => <SelLabel {...p} data={pnlData} fmt={fmtK} />} />
              </Bar>
              <Bar isAnimationActive={false} name="Net Income" dataKey="netIncome" fill={COLORS.violet} radius={[5, 5, 0, 0]} maxBarSize={26}>
                <LabelList dataKey="netIncome" position="top" content={(p) => <SelLabel {...p} data={pnlData} fmt={fmtK} />} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* ===================== EXPENSE ROW ===================== */}
      <div className="fd-grid-2">
        <Card
          title="Expense Categories"
          description={"Spend by category for " + selectedMonth + ", largest first."}
          empty={!has.expenseMonth}
        >
          <div className="fd-chart" style={{ height: Math.max(220, expenseCurrentMonth.length * 34 + 20) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={expenseCurrentMonth}
                margin={{ top: 4, right: 56, left: 8, bottom: 4 }}
              >
                <CartesianGrid horizontal={false} stroke="#EEEEEE" />
                <XAxis type="number" {...axisProps} tickFormatter={fmtK} />
                <YAxis type="category" dataKey="name" {...axisProps} width={132} tick={{ fill: "#6B7280", fontSize: 11 }} />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(108,111,232,0.06)" }} />
                <Bar isAnimationActive={false} dataKey="value" name="Expense" radius={[0, 5, 5, 0]} maxBarSize={22}>
                  {expenseCurrentMonth.map((d) => (
                    <Cell key={d.name} fill={catColor(d.name)} />
                  ))}
                  <LabelList dataKey="value" position="right" formatter={fmtK} style={{ fill: "#6B7280", fontSize: 11 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card
          title="Monthly Expense Trend by Category"
          description="Stacked spend per month across actuals."
          empty={!has.expenseTrend}
        >
          <div className="fd-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={expenseTrend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="#EEEEEE" />
                <XAxis dataKey="month" {...axisProps} />
                <YAxis {...axisProps} tickFormatter={fmtK} width={56} />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(108,111,232,0.06)" }} />
                <Legend wrapperStyle={{ fontSize: 11, color: "#6B7280", paddingTop: 8 }} />
                {categoryNames.map((name, i) => (
                  <Bar isAnimationActive={false}
                    key={name}
                    dataKey={name}
                    name={name}
                    stackId="exp"
                    fill={catColor(name)}
                    radius={i === categoryNames.length - 1 ? [5, 5, 0, 0] : [0, 0, 0, 0]}
                    maxBarSize={34}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* ===================== BALANCE SHEET OVERVIEW ===================== */}
      <SectionHeading
        title="Balance Sheet Overview"
        description="Position and liquidity across the full year. Months after actuals are projected and shown faded."
      />
      <Card
        title="Assets vs. Liabilities vs. Equity Over Time"
        description="Grouped totals across all twelve months."
        empty={!has.balance}
      >
        <div className="fd-chart fd-chart-tall">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={balanceData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="20%">
              <CartesianGrid vertical={false} stroke="#EEEEEE" />
              <XAxis dataKey="month" {...axisProps} />
              <YAxis {...axisProps} tickFormatter={fmtK} width={56} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(108,111,232,0.06)" }} />
              <Legend wrapperStyle={{ fontSize: 12, color: "#6B7280", paddingTop: 8 }} />
              <Bar isAnimationActive={false} name="Total Assets" dataKey="totalAssets" fill={COLORS.indigo} radius={[5, 5, 0, 0]} maxBarSize={22}>
                {balanceData.map((d, i) => (
                  <Cell key={i} fill={COLORS.indigo} fillOpacity={d.projected ? 0.3 : 1} />
                ))}
              </Bar>
              <Bar isAnimationActive={false} name="Total Liabilities" dataKey="totalLiabilities" fill={COLORS.coral} radius={[5, 5, 0, 0]} maxBarSize={22}>
                {balanceData.map((d, i) => (
                  <Cell key={i} fill={COLORS.coral} fillOpacity={d.projected ? 0.3 : 1} />
                ))}
              </Bar>
              <Bar isAnimationActive={false} name="Total Equity" dataKey="totalEquity" fill={COLORS.teal} radius={[5, 5, 0, 0]} maxBarSize={22}>
                {balanceData.map((d, i) => (
                  <Cell key={i} fill={COLORS.teal} fillOpacity={d.projected ? 0.3 : 1} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* ===================== INVENTORY + CURRENT ROW ===================== */}
      <div className="fd-grid-2">
        <Card
          title="Inventory Asset Trend"
          description={"Inventory value over the year. " + selectedShort + " marked; dashed line is the average."}
          empty={!has.inventory}
        >
          <div className="fd-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={inventoryData} margin={{ top: 24, right: 16, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="invGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLORS.indigo} stopOpacity={0.28} />
                    <stop offset="100%" stopColor={COLORS.indigo} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke="#EEEEEE" />
                <XAxis dataKey="month" {...axisProps} />
                <YAxis {...axisProps} tickFormatter={fmtK} width={56} />
                <Tooltip content={<ChartTooltip />} />
                <Area isAnimationActive={false}
                  type="monotone" dataKey="inventory" name="Inventory"
                  stroke={COLORS.indigo} strokeWidth={2} fill="url(#invGrad)"
                  dot={false} activeDot={{ r: 4 }}
                />
                <ReferenceLine
                  y={inventoryAvg} stroke="#9CA3AF" strokeDasharray="5 5"
                  label={{ value: "Avg " + fmtK(inventoryAvg), position: "insideTopRight", fill: "#9CA3AF", fontSize: 11 }}
                />
                <ReferenceDotForMonth data={inventoryData} month={selectedShort} dataKey="inventory" color={COLORS.indigo} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card
          title="Current Assets vs. Current Liabilities"
          description="Liquidity by month, with the selected-month current ratio."
          empty={!has.current}
        >
          <div className="fd-chart">
            <div className="fd-ratio-callout">
              <span className="fd-ratio-label">Current Ratio · {selectedShort}</span>
              <span className="fd-ratio-value">{currentRatio != null ? currentRatio.toFixed(2) + "x" : "—"}</span>
            </div>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={currentData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="22%">
                <CartesianGrid vertical={false} stroke="#EEEEEE" />
                <XAxis dataKey="month" {...axisProps} />
                <YAxis {...axisProps} tickFormatter={fmtK} width={56} />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(108,111,232,0.06)" }} />
                <Legend wrapperStyle={{ fontSize: 12, color: "#6B7280", paddingTop: 8 }} />
                <ReferenceArea x1={selectedShort} x2={selectedShort} fill={COLORS.blue} fillOpacity={0.07} />
                <Bar isAnimationActive={false} name="Current Assets" dataKey="currentAssets" fill={COLORS.blue} radius={[5, 5, 0, 0]} maxBarSize={20}>
                  {currentData.map((d, i) => (
                    <Cell key={i} fill={COLORS.blue} fillOpacity={d.projected ? 0.3 : 1} />
                  ))}
                </Bar>
                <Bar isAnimationActive={false} name="Current Liabilities" dataKey="currentLiabilities" fill={COLORS.coral} radius={[5, 5, 0, 0]} maxBarSize={20}>
                  {currentData.map((d, i) => (
                    <Cell key={i} fill={COLORS.coral} fillOpacity={d.projected ? 0.3 : 1} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* ===================== REVENUE + MARGIN ROW ===================== */}
      <div className="fd-grid-2">
        <Card
          title={"Revenue Breakdown by Source (" + ytdAbbr + ")"}
          description="Share of channel revenue, refunds, and shipping."
          empty={!has.revenueSource}
        >
          <div className="fd-chart fd-donut-wrap">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie isAnimationActive={false}
                  data={D.ytd.revenueBySource}
                  dataKey="value" nameKey="name"
                  cx="50%" cy="50%" innerRadius="62%" outerRadius="86%"
                  paddingAngle={2} stroke="none"
                  label={({ value }) => fmtPct((value / revenueSourceTotal) * 100)}
                  labelLine={false}
                >
                  {D.ytd.revenueBySource.map((_, i) => (
                    <Cell key={i} fill={donutColors[i % donutColors.length]} />
                  ))}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12, color: "#6B7280" }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="fd-donut-center">
              <div className="fd-donut-c-label">Gross Revenue</div>
              <div className="fd-donut-c-value">{fmtUSD(D.ytd.grossRevenue)}</div>
              <div className="fd-donut-c-sub">Net {fmtUSD(D.ytd.netRevenue)}</div>
            </div>
          </div>
        </Card>

        <Card
          title="Gross Margin % by Month"
          description={"Margin trend with the " + fmtPct(D.targets.grossMarginPct) + " target line."}
          empty={!has.margin}
        >
          <div className="fd-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={marginData} margin={{ top: 24, right: 40, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="marginGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLORS.teal} stopOpacity={0.28} />
                    <stop offset="100%" stopColor={COLORS.teal} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke="#EEEEEE" />
                <XAxis dataKey="month" {...axisProps} />
                <YAxis {...axisProps} tickFormatter={(v) => Math.round(v) + "%"} width={52} domain={["dataMin - 5", "dataMax + 5"]} />
                <Tooltip content={<ChartTooltip percent />} />
                <ReferenceLine
                  y={D.targets.grossMarginPct} stroke="#9CA3AF" strokeDasharray="5 5"
                  label={{ value: "Target " + fmtPct(D.targets.grossMarginPct), position: "insideTopRight", fill: "#9CA3AF", fontSize: 11 }}
                />
                <Area isAnimationActive={false}
                  type="monotone" dataKey="grossMarginPct" name="Gross Margin"
                  stroke={COLORS.teal} strokeWidth={2.5} fill="url(#marginGrad)"
                  dot={{ r: 3, fill: COLORS.teal, strokeWidth: 0 }}
                  activeDot={{ r: 5 }}
                >
                  <LabelList
                    dataKey="grossMarginPct"
                    content={(p) => <LastPointLabel {...p} data={marginData} fmt={fmtPct} />}
                  />
                </Area>
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* ===================== YTD BREAKDOWNS ROW ===================== */}
      <div className="fd-grid-2">
        <Card
          title={"Expense Categories " + ytdAbbr}
          description="Cumulative spend by category, largest first."
          empty={!has.expenseYtd}
        >
          <div className="fd-chart" style={{ height: Math.max(240, expenseYtd.length * 30 + 20) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={expenseYtd}
                margin={{ top: 4, right: 64, left: 8, bottom: 4 }}
              >
                <CartesianGrid horizontal={false} stroke="#EEEEEE" />
                <XAxis type="number" {...axisProps} tickFormatter={fmtK} />
                <YAxis type="category" dataKey="name" {...axisProps} width={134} tick={{ fill: "#6B7280", fontSize: 11 }} />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(108,111,232,0.06)" }} />
                <Bar isAnimationActive={false} dataKey="value" name={"Expense " + ytdAbbr} radius={[0, 5, 5, 0]} maxBarSize={20}>
                  {expenseYtd.map((d, i) => (
                    <Cell key={d.name} fill={CATEGORY_PALETTE[i % CATEGORY_PALETTE.length]} />
                  ))}
                  <LabelList dataKey="value" position="right" formatter={fmtK} style={{ fill: "#6B7280", fontSize: 11 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card
          title={"Top Vendors " + ytdAbbr}
          description="Highest-spend vendors for the period, from your transaction detail."
          empty={!has.vendors}
          emptyLabel="Add a 'Transaction List by Vendor' report to your sheet to see this"
        >
          <div className="fd-chart" style={{ height: Math.max(240, vendorYtd.length * 30 + 20) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={vendorYtd}
                margin={{ top: 4, right: 64, left: 8, bottom: 4 }}
              >
                <CartesianGrid horizontal={false} stroke="#EEEEEE" />
                <XAxis type="number" {...axisProps} tickFormatter={fmtK} />
                <YAxis type="category" dataKey="name" {...axisProps} width={140} tick={{ fill: "#6B7280", fontSize: 11 }} />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(45,190,154,0.08)" }} />
                <Bar isAnimationActive={false} dataKey="value" name={"Vendor spend " + ytdAbbr} radius={[0, 5, 5, 0]} maxBarSize={20}>
                  {vendorYtd.map((d, i) => (
                    <Cell key={d.name} fill={CATEGORY_PALETTE[i % CATEGORY_PALETTE.length]} />
                  ))}
                  <LabelList dataKey="value" position="right" formatter={fmtK} style={{ fill: "#6B7280", fontSize: 11 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <footer className="fd-footer">
        {D.company} · {D.title} · Last synced {lastSyncLabel}
      </footer>
    </div>
  );
}

/* =========================================================================
   CUSTOM LABEL RENDERERS
   ========================================================================= */
// Value label that only shows on the selected month's group (P&L chart).
function SelLabel(props) {
  const { x, y, width, value, index, data, fmt } = props;
  if (value == null || !data || !data[index] || !data[index].selected) return null;
  return (
    <text
      x={x + width / 2} y={y - 6}
      textAnchor="middle" fontSize={10} fontWeight={700} fill="#374151"
    >
      {fmt(value)}
    </text>
  );
}

// Label only on the last point of a series (gross-margin chart).
function LastPointLabel(props) {
  const { x, y, value, index, data, fmt } = props;
  if (!data || index !== data.length - 1 || value == null) return null;
  return (
    <g>
      <rect x={x + 6} y={y - 20} width={46} height={18} rx={4} fill="#2DBE9A" />
      <text x={x + 29} y={y - 7} textAnchor="middle" fontSize={11} fontWeight={700} fill="#fff">
        {fmt(value)}
      </text>
    </g>
  );
}

// Draws a marker dot + callout at a given month category. Rendered as a
// Recharts custom component so it inherits the cartesian scales.
function ReferenceDotForMonth({ data, month, dataKey, color, xAxisMap, yAxisMap, offset }) {
  // Recharts injects axis maps when this is a direct child of the chart.
  if (!xAxisMap || !yAxisMap) return null;
  const xAxis = xAxisMap[Object.keys(xAxisMap)[0]];
  const yAxis = yAxisMap[Object.keys(yAxisMap)[0]];
  if (!xAxis || !yAxis) return null;
  const row = data.find((d) => d.month === month);
  if (!row || row[dataKey] == null) return null;
  const cx = xAxis.scale(month) + (xAxis.scale.bandwidth ? xAxis.scale.bandwidth() / 2 : 0);
  const cy = yAxis.scale(row[dataKey]);
  if (cx == null || cy == null) return null;
  return (
    <g>
      <circle cx={cx} cy={cy} r={5} fill={color} stroke="#fff" strokeWidth={2} />
      <g transform={`translate(${cx}, ${cy - 14})`}>
        <rect x={-30} y={-18} width={60} height={18} rx={4} fill={color} />
        <text x={0} y={-5} textAnchor="middle" fontSize={11} fontWeight={700} fill="#fff">
          {fmtK(row[dataKey])}
        </text>
      </g>
    </g>
  );
}

/* =========================================================================
   STYLES — one block, scoped under .fin-dash
   ========================================================================= */
const CSS = `
.fin-dash {
  --bg: #FBFBFA;
  --card: #FFFFFF;
  --border: #ECECEC;
  --ink: #0D0D0D;
  --muted: #6B7280;
  --faint: #9CA3AF;
  --pos: #16A34A;
  --neg: #DC2626;
  font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: var(--bg);
  color: var(--ink);
  padding: 28px;
  box-sizing: border-box;
  -webkit-font-smoothing: antialiased;
}
.fin-dash * { box-sizing: border-box; }

/* ---------- Header ---------- */
.fd-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 28px;
  flex-wrap: wrap;
}
.fd-company {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--faint);
  margin-bottom: 6px;
}
.fd-title { font-size: 26px; font-weight: 800; margin: 0 0 8px; letter-spacing: -0.02em; }
.fd-subtitle { font-size: 13.5px; color: var(--muted); margin: 0; max-width: 640px; line-height: 1.5; }
.fd-disclaimer { font-size: 11.5px; color: var(--faint); margin: 4px 0 0; max-width: 860px; line-height: 1.5; }
.fd-header-right { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.fd-filter {
  display: inline-flex; align-items: center; gap: 6px;
  height: 38px; padding: 0 14px;
  border: 1px solid var(--border); border-radius: 10px;
  background: #fff; font-size: 13px; font-weight: 600; color: var(--muted);
}
.fd-filter-ico { font-size: 11px; }
.fd-select-wrap { position: relative; display: inline-flex; }
.fd-select-wrap::after {
  content: "▾"; position: absolute; right: 12px; top: 50%;
  transform: translateY(-50%); pointer-events: none; color: var(--muted); font-size: 11px;
}
.fd-select {
  appearance: none; -webkit-appearance: none;
  height: 38px; padding: 0 34px 0 14px;
  border: 1px solid var(--border); border-radius: 10px;
  background: #fff; font-family: inherit; font-size: 13px; font-weight: 700;
  color: var(--ink); cursor: pointer;
}
.fd-select:focus { outline: 2px solid rgba(108,111,232,0.4); outline-offset: 0; }
.fd-sync { font-size: 11px; color: var(--faint); width: 100%; text-align: right; }

/* ---------- Section heading ---------- */
.fd-section-head { margin: 28px 0 14px; }
.fd-section-title { font-size: 20px; font-weight: 800; margin: 0 0 4px; letter-spacing: -0.01em; }
.fd-section-desc { font-size: 13px; color: var(--muted); margin: 0; }

/* ---------- KPI cards ---------- */
.fd-kpi-row {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px;
}
.fd-kpi {
  background: var(--card); border: 1px solid var(--border); border-radius: 14px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06); padding: 18px 20px;
}
.fd-kpi-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.fd-kpi-label {
  font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: var(--faint);
}
.fd-kpi-icon {
  width: 26px; height: 26px; display: inline-flex; align-items: center; justify-content: center;
  border-radius: 8px; background: #F4F4F6; color: var(--muted); font-size: 13px;
}
.fd-kpi-value { font-size: 30px; font-weight: 800; letter-spacing: -0.02em; line-height: 1.1; }
.fd-kpi-delta { font-size: 12.5px; font-weight: 600; margin-top: 8px; color: var(--muted); }
.fd-kpi-tag { font-size: 11px; color: var(--faint); margin-top: 6px; }
.fd-tone-pos { color: var(--pos); }
.fd-tone-neg { color: var(--neg); }
.fd-tone-muted { color: var(--muted); }

/* ---------- Cards ---------- */
.fd-card {
  background: var(--card); border: 1px solid var(--border); border-radius: 14px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06); padding: 20px; margin-top: 16px;
}
.fd-card-head { margin-bottom: 12px; }
.fd-card-title { font-size: 15px; font-weight: 700; margin: 0 0 3px; }
.fd-card-desc { font-size: 12.5px; color: var(--muted); margin: 0; }

.fd-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }

/* ---------- Chart wrappers ---------- */
.fd-chart { width: 100%; height: 300px; position: relative; }
.fd-chart-tall { height: 360px; }
.fd-chart-short { height: 64px; }

/* ---------- Empty state (no data for a series) ---------- */
.fd-empty {
  width: 100%; min-height: 240px;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 8px; color: var(--faint); font-size: 13px; font-weight: 600;
}
.fd-empty-ico { font-size: 24px; opacity: 0.45; line-height: 1; }

/* ---------- Whole-dashboard empty / not-connected state ---------- */
.fd-empty-shell { min-height: 340px; display: flex; align-items: center; justify-content: center; }
.fd-noconnect { text-align: center; max-width: 380px; padding: 32px; }
.fd-noconnect-ico { font-size: 40px; opacity: 0.35; margin-bottom: 12px; line-height: 1; }
.fd-noconnect-title { font-size: 17px; font-weight: 700; color: var(--ink); margin-bottom: 6px; }
.fd-noconnect-sub { font-size: 13.5px; color: var(--muted); line-height: 1.5; }

/* ---------- Donut center ---------- */
.fd-donut-wrap { display: flex; align-items: center; justify-content: center; }
.fd-donut-center {
  position: absolute; left: 50%; top: 46%; transform: translate(-50%, -50%);
  text-align: center; pointer-events: none;
}
.fd-donut-c-label { font-size: 11px; color: var(--faint); font-weight: 600; }
.fd-donut-c-value { font-size: 22px; font-weight: 800; letter-spacing: -0.02em; margin-top: 2px; }
.fd-donut-c-sub { font-size: 12px; color: var(--muted); margin-top: 2px; }

/* ---------- Current ratio callout ---------- */
.fd-ratio-callout {
  position: absolute; right: 12px; top: 0; z-index: 2;
  display: flex; flex-direction: column; align-items: flex-end;
  background: rgba(77,150,240,0.08); border: 1px solid rgba(77,150,240,0.25);
  border-radius: 10px; padding: 6px 12px;
}
.fd-ratio-label { font-size: 10px; color: var(--muted); font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; }
.fd-ratio-value { font-size: 18px; font-weight: 800; color: #2563EB; }

/* ---------- COGS panel ---------- */
.fd-cogs { padding-top: 4px; }
.fd-cogs-figures { display: flex; align-items: baseline; gap: 12px; margin-bottom: 14px; }
.fd-cogs-value { font-size: 30px; font-weight: 800; letter-spacing: -0.02em; }
.fd-cogs-sub { font-size: 13px; font-weight: 600; color: var(--coral, #F26D6D); color: #DC2626; }
.fd-cogs-scale { display: flex; justify-content: space-between; font-size: 11px; color: var(--faint); margin-top: 6px; }

/* ---------- Tooltip ---------- */
.fd-tooltip {
  background: #fff; border: 1px solid var(--border); border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.1); padding: 10px 12px; min-width: 150px;
}
.fd-tooltip-title { font-size: 12px; font-weight: 700; margin-bottom: 6px; color: var(--ink); }
.fd-tooltip-row { display: flex; align-items: center; gap: 8px; font-size: 12px; padding: 2px 0; }
.fd-tooltip-dot { width: 9px; height: 9px; border-radius: 3px; flex: 0 0 auto; }
.fd-tooltip-name { color: var(--muted); flex: 1; }
.fd-tooltip-val { font-weight: 700; color: var(--ink); }

/* ---------- Footer ---------- */
.fd-footer { margin-top: 28px; font-size: 11.5px; color: var(--faint); text-align: center; }

/* ---------- Responsive ---------- */
@media (max-width: 900px) {
  .fd-kpi-row { grid-template-columns: repeat(2, 1fr); }
  .fd-grid-2 { grid-template-columns: 1fr; }
}
@media (max-width: 560px) {
  .fin-dash { padding: 16px; }
  .fd-kpi-row { grid-template-columns: 1fr; }
}
`;
